import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Icon } from "components/ui/icon";
// import { Button } from "components/ui/button";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { toast } from "sonner";
import { signOutAction } from "action/auth-action";
import { routes } from "router";
import { useUserData } from "store/user-data";
import { useAuth } from 'context/auth-context';

const ProfileInfo = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();

    function auth() {
        const userData = useUserData.getState().userData;
        const displayName = userData?.username?.includes("@")
            ? userData.username.split("@")[0]
            : userData?.username;

        // Split by spaces, dots, underscores, hyphens, OR case transitions (camelCase/PascalCase)
        const parts = displayName?.split(/[\s._-]+|(?=[A-Z])/).filter(Boolean) || [];
        const initials = parts.length > 1
            ? (parts[0][0] + parts[1][0]).toUpperCase()
            : parts.length === 1
                ? parts[0].slice(0, 2).toUpperCase()
                : "SB";

        return {
            user: {
                name: userData?.username,
                email: userData?.email ?? userData?.phone_number,
                image: userData?.business_logo || "",
                initials
            },
        };
    }

    const session = auth();

    function logOut() {
        const toastId = toast.loading("Logging out...");
        signOutAction()
            .then((response) => {
                if (response.status === 200) {
                    toast.info("You've logged out!", { id: toastId });
                    logout();
                    navigate(routes.Login.path);
                } else {
                    toast.error(response.error, { id: toastId });
                }
            })
            .catch((err: any) => {
                toast.error("Failed to logout, An error occurred", { id: toastId });
            });
    }

    function handleEmailClick() {
        window.location.href = "mailto:support@storbolt.com";
    }

    function handleCallClick() {
        const phoneNumber = "+2344567890"; // TODO: Replace with real phone number
        navigator.clipboard.writeText(phoneNumber).then(
            () => {
                toast.success("Phone number copied to clipboard");
            },
            () => {
                toast.error("Failed to copy phone number");
            }
        );
    }

    function handleWhatsappClick() {
        const phoneNumber = "+2344567890"; // TODO: Replace with real phone number
        const message = "Hello I have an issue";
        const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
            message
        )}`;
        window.open(url, "_blank");
    }

    const supportItems = [
        {
            name: "Email",
            action: handleEmailClick,
        },
        {
            name: "Call",
            action: handleCallClick,
        },
        {
            name: "WhatsApp",
            action: handleWhatsappClick,
        },
    ];

    return (
        <div>
            <DropdownMenu>
                <DropdownMenuTrigger asChild className="cursor-pointer">
                    <div className="flex items-center gap-2.5 rounded-full py-1 ps-1 pe-1 text-default-800 transition-colors hover:bg-default-100 md:pe-3">
                        <span className="flex h-8 w-8 flex-none items-center justify-center overflow-hidden rounded-full bg-sage font-display text-xs font-semibold text-sage-foreground">
                            {session.user.image ? (
                                <img src={session.user.image} alt="" className="h-full w-full object-cover" />
                            ) : (
                                session.user.initials
                            )}
                        </span>
                        {/*<Avatar className="hidden lg:block flex-col items-center">*/}
                        {/*    <AvatarImage src={session.user.image} />*/}
                        {/*    <AvatarFallback>SB</AvatarFallback>*/}
                        {/*</Avatar>*/}

                        <div className="text-sm font-medium md:block hidden">
                            {session.user.name}
                        </div>
                        <span className="text-base md:inline-block hidden">
                            <Icon icon="heroicons-outline:chevron-down"></Icon>
                        </span>
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 p-0" align="end">
                    <DropdownMenuLabel className="flex gap-2 items-center mb-1 p-3">
                        <Avatar className="hidden lg:block flex-col items-center">
                            <AvatarImage src={session.user.image} />
                            <AvatarFallback>{session.user.initials}</AvatarFallback>
                        </Avatar>

                        <div>
                            <div className="text-sm font-medium text-default-800 capitalize">
                                {session.user.name}
                            </div>
                            {session.user.email !== session.user.name && (
                                <a
                                    href={routes.Home.path}
                                    className="text-xs text-default-600 hover:text-primary"
                                >
                                    {session.user.email}
                                </a>
                            )}
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuGroup>
                        {[
                            {
                                name: "Profile",
                                icon: "heroicons:user",
                                to: `${routes.ProfilePage.path}`,
                            },
                            {
                                name: "Billing",
                                icon: "heroicons:megaphone",
                                to: `${routes.Home.path}`,
                            },
                            {
                                name: "Settings",
                                icon: "heroicons:paper-airplane",
                                to: `${routes.Home.path}`,
                            },
                        ].map((item, index) => (
                            <a href={item.to} key={`info-menu-${index}`} className="cursor-pointer">
                                <DropdownMenuItem className="flex items-center gap-2 text-sm font-medium text-default-600 capitalize px-3 py-1.5 cursor-pointer">
                                    <Icon icon={item.icon} className="w-4 h-4" />
                                    {item.name}
                                </DropdownMenuItem>
                            </a>
                        ))}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <a href="/team" className="cursor-pointer">
                            <DropdownMenuItem className="flex items-center gap-2 text-sm font-medium text-default-600 capitalize px-3 py-1.5 cursor-pointer">
                                <Icon icon="heroicons:user-group" className="w-4 h-4" />
                                Team
                            </DropdownMenuItem>
                        </a>

                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="flex items-center gap-2 text-sm font-medium text-default-600 capitalize px-3 py-1.5 cursor-pointer">
                                <Icon icon="heroicons:phone" className="w-4 h-4" />
                                Support
                            </DropdownMenuSubTrigger>
                            <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                    {supportItems.map((item, index) => (
                                        <DropdownMenuItem
                                            key={`support-item-${index}`}
                                            onClick={item.action}
                                            className="text-sm font-medium text-default-600 capitalize px-3 py-1.5 cursor-pointer"
                                        >
                                            {item.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuSubContent>
                            </DropdownMenuPortal>
                        </DropdownMenuSub>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator className="mb-0 dark:bg-background" />
                    <DropdownMenuItem className="flex items-center gap-2 text-sm font-medium text-default-600 capitalize my-1 px-3 cursor-pointer">
                        <button
                            type="button"
                            className="w-full flex items-center gap-2"
                            onClick={logOut}
                        >
                            <Icon icon="heroicons:power" className="w-4 h-4" />
                            Log out
                        </button>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

export default ProfileInfo;
